import type { CartUI } from '@libs/common-node';

import { CART_ERROR_MESSAGES, COMPONENTS } from '../../constants';
import type { RefetchCartStateOptions } from '../../types';
import { getCartById, logConsoleError } from '../../utils';

type ResolvedCartForAddItem = Readonly<{
    cart: CartUI | null;
    /** True when the cart this tab was holding is no longer usable and local state must drop it. */
    didDropHeldCart: boolean;
}>;

/**
 * Compares the browser-wide cart id cookie against the cart id a tab last synced with.
 *
 * The cookie is shared by every tab on the domain, so it diverges from a tab's in-memory cart
 * whenever another tab checks out, signs out, or merges carts. Both directions matter: a tab
 * holding a cart the cookie no longer names would mutate a cart that may be gone, and a tab
 * holding no cart while the cookie names one would create a second cart and orphan the first.
 */
export const isCartIdentityStale = (cartIdCookie?: string, knownCartId?: string): boolean =>
    (cartIdCookie || undefined) !== (knownCartId || undefined);

/**
 * Confirms a cart still exists before an add-to-cart rejection is treated as a missing cart.
 * `getCartById` reports a missing cart as a response body without an `id` rather than throwing.
 */
export const cartStillExists = async (cartId: string): Promise<boolean> => {
    try {
        const existingCart = await getCartById({ cartId });

        return Boolean(existingCart?.id);
    } catch (error) {
        logConsoleError(JSON.stringify(error), {
            component: COMPONENTS.CART_PROVIDER,
            severity: 'HIGH',
            additionalInfo: `${CART_ERROR_MESSAGES.CART_NOT_FOUND} | Could not confirm whether cart ${cartId} still exists`,
        });

        // a failed lookup says nothing about the cart, so assume it is intact and let the original
        // add-to-cart failure surface instead of starting a duplicate cart.
        return true;
    }
};

/**
 * Picks the cart an add-to-cart should target, reconciling against the shared cookie first so a tab
 * never mutates a cart another tab has already checked out, signed out of, or merged away.
 */
export const resolveCartForAddItem = async ({
    heldCart,
    cartIdCookie,
    refetchCart,
}: {
    heldCart?: CartUI | null;
    cartIdCookie?: string;
    refetchCart: (options: RefetchCartStateOptions) => Promise<CartUI | null>;
}): Promise<ResolvedCartForAddItem> => {
    if (!isCartIdentityStale(cartIdCookie, heldCart?.id)) {
        return { cart: heldCart ?? null, didDropHeldCart: false };
    }

    const reconciledCart = cartIdCookie
        ? await refetchCart({ cartId: cartIdCookie, preserveUpdatingState: true })
        : null;

    return { cart: reconciledCart, didDropHeldCart: !reconciledCart };
};

/**
 * Decides whether a failed add-to-cart should be retried as a brand new cart.
 *
 * The API also answers without a cart id for business rejections such as exceeding max quantity, so
 * the cart is verified as genuinely missing first. Retrying those would create a second cart and
 * orphan the shopper's real one.
 */
export const shouldRetryAddItemAsNewCart = async (
    addItemResponse: CartUI | null,
    attemptedCart?: CartUI | null
): Promise<boolean> => {
    if (addItemResponse?.id || !attemptedCart?.id) {
        return false;
    }

    return !(await cartStillExists(attemptedCart.id));
};
